import { useEffect, useState } from 'react';
import { Button, Card, CardHeader, Checkbox, Divider, Grid, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { makeStyles } from "@mui/styles";
import { formatUwi } from '../helpers/formatUwi';
import { FixedSizeList } from 'react-window';
import { useSelector } from 'react-redux';
import { selectWells } from '../reducers/wellSlice';
import { selectProjects } from '../reducers/projectSlice';
import { useSocket } from '../context/useSocket';
import { selectUser } from '../reducers/userSlice';

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
};

function intersection(a, b) {
  return a.filter((value) => b.indexOf(value) !== -1);
};

function union(a, b) {
  return [...a, ...not(b, a)];
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
    marginTop: 10,
    width: '100%',
    position: 'relative'
  },
  listHeader: {
    position: 'fixed'
  },
  listContainer: {
    width: '45%',
  },
  list: {
    maxHeight: 300,
    minHeight: 300,
    overflow: 'auto',
  }
}));

export const EconomicsWellsTransferList = ({ wells, uwis, setUwis }) => {

  const classes = useStyles();
  const allWells = useSelector(selectWells);
  const user = useSelector(selectUser);
  const projects = useSelector(selectProjects);
  const currentProject = projects?.byId?.[projects?.currentProjectId];

  const { socket } = useSocket();

  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(() => not(wells, uwis));

  const leftChecked = intersection(checked, left);
  const uwisChecked = intersection(checked, uwis);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    };

    // send a well process request if its not processed
    if (allWells?.byUwi?.[value]?.status !== 'processed') {
      sendProcessRequest([value], false);
    };

    setChecked(newChecked);
  };

  const numberOfChecked = (items) => intersection(checked, items).length;

  const handleToggleAll = (items) => () => {
    if (numberOfChecked(items) === items.length) {
      setChecked(not(checked, items));
    } else {
      setChecked(union(checked, items));
    };
  };

  const handleCheckedRight = () => {
    setUwis(() => uwis.concat(leftChecked));
    setLeft(() => not(left, leftChecked));
    setChecked(() => not(checked, leftChecked));
  };

  const handleAllRight = () => {
    setUwis(() => uwis.concat(left));
    setLeft([]);

    const unprocessedWells = [];
    uwis?.forEach(uwi => {
      if (wells?.byUwi?.[uwi]?.status !== 'processed') {
        unprocessedWells.push(uwi);
      };
    });

    if (unprocessedWells?.length > 0) {
      sendProcessRequest(unprocessedWells, false);
    };
  };

  const handleCheckedLeft = () => {
    setLeft(() => left.concat(uwisChecked));
    setUwis(() => not(uwis, uwisChecked));
    setChecked(() => not(checked, uwisChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(uwis));
    setUwis([]);
  };

  useEffect(() => {
    setLeft(wells);
  }, [wells]);

  function renderLeftSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={left[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(left[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(left[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography noWrap variant="body2" >{formatUwi(left[index])}</Typography>} 
          secondary={<Typography variant="caption" color="textSecondary" noWrap>{getWellStatus(allWells?.byUwi?.[left[index]])}</Typography>} 
        />
      </ListItem>
    );
  };
  function renderRightSourceRow(props) {
    const { index, style } = props;
    return (
      <ListItem
        key={uwis[index]}
        style={{...style, borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center'}}
        role="listitem"
        button
        onClick={handleToggle(uwis[index])}
        dense
      >
        <ListItemIcon>
          <Checkbox
            checked={checked.indexOf(uwis[index]) !== -1}
            tabIndex={-1}
            disableRipple
            color="primary"
          />
        </ListItemIcon>
        <ListItemText 
          primary={<Typography noWrap variant="body2">{formatUwi(uwis[index])}</Typography>} 
          secondary={<Typography variant="caption" color="textSecondary" noWrap>{getWellStatus(allWells?.byUwi?.[uwis[index]])}</Typography>} 
        />

      </ListItem>
    );
  };

  const customList = (title, items, side) => (
    <Card style={{padding: 0, margin: 0, width: '100%'}}>
      <CardHeader
        avatar={
          <Checkbox
            onClick={handleToggleAll(items)}
            checked={numberOfChecked(items) === items.length && items.length !== 0}
            indeterminate={
              numberOfChecked(items) !== items.length && numberOfChecked(items) !== 0
            }
            disabled={items.length === 0}
            color="primary"
          />
        }
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        style={{paddingBottom: 10}}
      />
      <Divider />
      <FixedSizeList
        height={300}
        width={'100%'}
        itemSize={54}
        itemCount={side === 'left' ? left?.length : uwis?.length}
      >
        {side === 'left' ? renderLeftSourceRow : renderRightSourceRow}
      </FixedSizeList>
    </Card>
  );

  return (
    <Grid container spacing={2} justifyContent="center" alignItems="center" className={classes.root}>
      <Grid item className={classes.listContainer}>{customList('All Wells', left, 'left')}</Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllRight}
            disabled={left.length === 0}
          >
            ≫
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedRight}
            disabled={leftChecked.length === 0}
          >
            &gt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleCheckedLeft}
            disabled={uwisChecked.length === 0}
          >
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5 }}
            variant="outlined"
            size="small"
            onClick={handleAllLeft}
            disabled={uwis.length === 0}
          >
            ≪
          </Button>
        </Grid>
      </Grid>
      <Grid item className={classes.listContainer}>{customList('Economics Forecast', uwis, 'right')}</Grid>
    </Grid>
  );

  function getWellStatus(well) {
    try {
      if (well?.status === 'processed') {
        let fluids = well.production?.fluids || [];
        return fluids.join(', ').toUpperCase();
      };
      return well.status.toUpperCase();
    } catch (error) {
      return "";
    };
  };

  function sendProcessRequest(uwis, reFit) {
    try {
      let localParams = [];
      currentProject?.wells?.forEach(w => {
        let newWell = {...w};
        let wellParams = {...newWell.params};
        const wellFluids = Object.keys(wellParams)?.filter(f => f !== 'cutoff');
        
        for (let fluid of wellFluids) {
          wellParams[fluid] = {...wellParams[fluid], outliers: []};
        };
        localParams.push({...newWell, params: wellParams});
      });

      if (socket && socket.readyState === 1) {
        socket.send(JSON.stringify({
          messageType: 'process', 
          payload: uwis || [], 
          userEmail: user?.value?.email, 
          params: {},
          localParams,
          reFit,
          assistedForecast: false, 
          tcParams: null
        }));
      };
    } catch (error) {
      console.log(error);
    };
  };
};
